import { downloadAppleWalletPassByOrder, downloadAppleWalletPassByHash } from '../apiEndpoints';

/**
 * Apple Wallet utility functions for downloading passes
 */

export interface AppleWalletPassData {
    orderId?: string;
    hashedId?: string;
    qrCode?: string;
    ticketIndex?: number;
}

/**
 * Downloads an Apple Wallet pass for a ticket
 * @param passData - The pass data containing order/hashed ID and ticket index
 * @returns Promise that resolves when download is complete
 */
export async function downloadAppleWalletPass(passData: AppleWalletPassData): Promise<void> {
    try {
        const { orderId, hashedId, qrCode, ticketIndex } = passData;

        if (!orderId && !hashedId) {
            throw new Error('Either orderId or hashedId must be provided');
        }

        let passBlob: Blob;
        let filename: string;

        // Determine which API to use based on available data
        if (orderId && ticketIndex !== undefined) {
            passBlob = await downloadAppleWalletPassByOrder(orderId, ticketIndex);
            filename = `ticket-${orderId}-${ticketIndex}.pkpass`;
        } else if (hashedId && qrCode) {
            passBlob = await downloadAppleWalletPassByHash(hashedId, qrCode);
            filename = `ticket-${hashedId}-${qrCode}.pkpass`;
        } else {
            throw new Error('Invalid combination of parameters provided');
        }

        // Create a download link
        const downloadUrl = window.URL.createObjectURL(passBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;

        // Trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
        console.error('Error downloading Apple Wallet pass:', error);
        throw error;
    }
}

/**
 * Checks if the current device supports Apple Wallet
 * @returns boolean indicating if Apple Wallet is supported
 */
export function isAppleWalletSupported(): boolean {
    // Check if we're on iOS or if the device supports Apple Wallet
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);

    // Also check for Apple Wallet API support
    const hasWalletAPI = 'AddPassesViewController' in window || 'PKAddPassesViewController' in window;

    return isIOS || isSafari || hasWalletAPI;
}

/**
 * Checks if the current device supports Google Wallet
 * @returns boolean indicating if Google Wallet is supported
 */
export function isGoogleWalletSupported(): boolean {
    // Check if we're on Android or if the device supports Google Wallet
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = /android/.test(userAgent);
    const hasWalletAPI = 'addWalletPass' in window;
    return isAndroid || hasWalletAPI;
}


/**
 * Shows a user-friendly message about Apple Wallet support
 * @returns string with appropriate message
 */
export function getAppleWalletSupportMessage(): string {
    if (isAppleWalletSupported()) {
        return 'Add to Apple Wallet';
    } else {
        return 'Download for Apple Wallet';
    }
}
